import {
  Card,
  Chip,
  Input,
  Select,
  Textarea,
  Toggle,
  Typography,
} from "@mg/dali/src";
import { ArrowLeft, Copy, Envelope, Spinner } from "@phosphor-icons/react";
import { Tabs, Button, Dialog, Flex } from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { createRoute, redirect, useNavigate } from "@tanstack/react-router";
import { type ChangeEvent, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Markdown } from "../../../components/Markdown";

/** Strip <body>…</body> wrapper so rehype-raw can process the inner HTML */
function stripBody(html: string): string {
  return html.replace(/<\/?body[^>]*>/gi, "");
}
import { useUI } from "../../../contexts/ui";
import { requiresAuth, canManageUsers } from "../../../utils/auth";
import {
  useIntegrationSettings,
  useUpdateIntegrationSettings,
  useIntegrationHealth,
  useTriggerIntegrationTest,
  useIntegrationEventStatus,
  useRecentIntegrationTasks,
  useAsanaProjects,
  useRegisterAsanaWebhook,
  useRemoveAsanaWebhook,
} from "../../../utils/queries/enterprises";
import { authLayoutRoute } from "../../auth-layout/route";
import { ticketsRoute } from "../../tickets/route";
import { settingsRoute } from "../route";

export const integrationSettingsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "integration-settings",
  beforeLoad() {
    if (!canManageUsers()) {
      throw redirect({ to: ticketsRoute.to } as any);
    }
    requiresAuth();
  },
  component: IntegrationSettings,
});

interface TriggerConfig {
  eventType?: string;
  statusName?: string;
  skipPunttUserCheck?: boolean;
  assignSelfOnTrigger?: boolean;
}

interface CommentBehavior {
  postProgressComments?: boolean;
  postCompletionComments?: boolean;
  skipAllComments?: boolean;
  skipLinkUpdates?: boolean;
}

interface DesignReviewRule {
  descriptionContains: string;
  tagUsersOnCompletion?: boolean;
  skipStatusUpdate?: boolean;
}

interface EmailReplySettings {
  replyWithLink?: boolean;
  replyWithComments?: boolean;
  replyPrompt?: string;
}

interface IntegrationConfig {
  wrike?: {
    enabled?: boolean;
    user?: string;
    responseActions?: any;
    triggerConfig?: TriggerConfig;
    commentBehavior?: CommentBehavior;
    designReviewRules?: DesignReviewRule[];
  };
  asana?: {
    enabled?: boolean;
    emailEnabled?: boolean;
    allowedProjectGids?: string[];
    commentBehavior?: CommentBehavior;
    designReviewRules?: DesignReviewRule[];
    webhooks?: {
      webhookId: string;
      projectGid: string;
      projectName: string;
      active: boolean;
      createdAt?: string;
    }[];
  };
  email?: {
    enabled?: boolean;
    replySettings?: EmailReplySettings;
    allowedDomains?: string[];
    allowedSenderPatterns?: string[];
  };
}

type IntegrationTab = "wrike" | "asana" | "email";

const DEFAULT_REPLY_PROMPT =
  "Summarize the AI review findings concisely. Include the number of issues found, their severity, and key recommendations.";

const EVENT_TYPE_OPTIONS = [
  { label: "Task Status Changed", value: "TaskStatusChanged" },
  { label: "Task Created", value: "TaskCreated" },
  { label: "Task Updated", value: "TaskUpdated" },
];

function HealthBadge({ status }: { status: any; loading?: boolean }) {
  const { t } = useTranslation("ui");
  if (!status) return null;

  if (status.connected && status.status === "ACTIVE") {
    return (
      <span className="ml-2 inline-flex items-center gap-1">
        <span className="bg-green-500 size-2 rounded-full" />
        <Typography size="xs" className="text-green-600">
          {t("settings.integrationSettingsRoute.connected")}
        </Typography>
      </span>
    );
  }

  if (!status.connected) {
    return (
      <span className="ml-2 inline-flex items-center gap-1">
        <span className="bg-red-500 size-2 rounded-full" />
        <Typography size="xs" className="text-red-500">
          {t("settings.integrationSettingsRoute.notConnected")}
        </Typography>
      </span>
    );
  }

  return (
    <span className="ml-2 inline-flex items-center gap-1">
      <span className="bg-yellow-500 size-2 rounded-full" />
      <Typography size="xs" className="text-yellow-600">
        {status.status}
      </Typography>
    </span>
  );
}

function EmailHealthBadge({ status }: { status: any }) {
  const { t } = useTranslation("ui");
  if (!status) return null;
  const ok = status.enabled && status.postmarkConfigured;
  return (
    <span className="ml-2 inline-flex items-center gap-1">
      <span
        className={`size-2 rounded-full ${ok ? "bg-green-500" : "bg-red-500"}`}
      />
      <Typography size="xs" className={ok ? "text-green-600" : "text-red-500"}>
        {ok
          ? t("common:status.active")
          : status.enabled
            ? t("settings.integrationSettingsRoute.postmarkNotConfigured")
            : t("common:status.disabled")}
      </Typography>
    </span>
  );
}

function connectionChipVariant(health: any) {
  if (!health || !health.connected) return "carbon-light" as const;
  switch (health.status) {
    case "ACTIVE":
      return "malachite" as const;
    case "FAILED":
    case "EXPIRED":
      return "vermillion" as const;
    case "INITIATED":
      return "cadmium" as const;
    default:
      return "carbon-light" as const;
  }
}

function connectionLabel(health: any) {
  if (!health) return "Not configured";
  if (!health.connected) return "Not connected";
  return health.status;
}

function ConnectionCard({ name, conn }: { name: string; conn: any }) {
  const { t } = useTranslation("ui");
  return (
    <div className="grid gap-2 rounded border p-3">
      <div className="flex items-center justify-between">
        <Typography size="sm" weight="bold">
          {name}
        </Typography>
        <div className="flex items-center gap-2">
          <Chip
            label={connectionLabel(conn)}
            variant={connectionChipVariant(conn)}
            size="sm"
          />
          {conn?.isDisabled && (
            <Chip
              label={t("common:status.disabled")}
              variant="vermillion"
              size="sm"
            />
          )}
        </div>
      </div>
      {conn?.connected && (
        <div className="grid gap-1">
          {conn.accountId && (
            <div className="flex items-center justify-between">
              <Typography size="xs" className="text-gray-500">
                {t("settings.integrationSettingsRoute.account")}
              </Typography>
              <button
                data-testid="connection-card-copy-account-id"
                aria-label={t(
                  "settings.integrationSettingsRoute.copyAccountId",
                )}
                className="text-gray-600 hover:text-gray-800 flex items-center gap-1 text-xs"
                onClick={() => navigator.clipboard.writeText(conn.accountId)}
              >
                <span className="font-mono max-w-[140px] truncate">
                  {conn.accountId}
                </span>
                <Copy size={12} />
              </button>
            </div>
          )}
          {conn.updatedAt && (
            <Typography size="xs" className="text-gray-400">
              Updated {new Date(conn.updatedAt).toLocaleDateString()}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
}

function ComposioStatusCard({
  wrike,
  asana,
  email,
}: {
  wrike: any;
  asana: any;
  email: any;
}) {
  const { t } = useTranslation("ui");
  return (
    <Card className="mb-4">
      <div className="grid gap-3 p-4">
        <Typography size="sm" weight="bold">
          {t("settings.integrationSettingsRoute.connectionStatus")}
        </Typography>
        <div className="grid grid-cols-3 gap-4">
          <ConnectionCard name="Wrike" conn={wrike} />
          <ConnectionCard name="Asana" conn={asana} />
          <div className="grid gap-2 rounded border p-3">
            <div className="flex items-center justify-between">
              <Typography size="sm" weight="bold">
                {t("common:labels.email")}
              </Typography>
              <Chip
                label={
                  email?.enabled
                    ? email?.postmarkConfigured
                      ? "Active"
                      : "No Postmark"
                    : "Disabled"
                }
                variant={
                  email?.enabled && email?.postmarkConfigured
                    ? "malachite"
                    : "carbon-light"
                }
                size="sm"
              />
            </div>
            {email?.aiEmailAddress && (
              <div className="flex items-center justify-between">
                <Typography size="xs" className="text-gray-500">
                  {t("settings.integrationSettingsRoute.aiAddress")}
                </Typography>
                <button
                  data-testid="connection-card-copy-ai-email"
                  aria-label={t(
                    "settings.integrationSettingsRoute.copyAiEmailAddress",
                  )}
                  className="text-gray-600 hover:text-gray-800 flex items-center gap-1 text-xs"
                  onClick={() =>
                    navigator.clipboard.writeText(email.aiEmailAddress)
                  }
                >
                  <span className="font-mono max-w-[140px] truncate">
                    {email.aiEmailAddress}
                  </span>
                  <Copy size={12} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function RuleBehaviorPreview({ rule }: { rule: DesignReviewRule }) {
  const parts: string[] = [];
  if (rule.tagUsersOnCompletion) {
    parts.push("Posts comment with @-mentions + review link");
  } else {
    parts.push("Posts plain review link, sets status to Completed");
  }
  if (rule.skipStatusUpdate) {
    parts.push("does NOT update status");
  }

  return (
    <div className="bg-gray-50 rounded p-2">
      <Typography size="xs" className="text-gray-600">
        On match: {parts.join(", ")}
      </Typography>
    </div>
  );
}

function ReplyList({
  replies,
  replyCardClass,
}: {
  replies?: { type: string; content: string }[];
  replyCardClass: (type: string) => string;
}) {
  if (!replies || replies.length === 0) return null;
  return (
    <div className="grid gap-2">
      {replies.map((reply, i) => (
        <div key={i} className={replyCardClass(reply.type)}>
          <Markdown className="text-xs">{stripBody(reply.content)}</Markdown>
        </div>
      ))}
    </div>
  );
}

function TestIntegrationDialog({
  platform,
  open,
  onOpenChange,
}: {
  platform: IntegrationTab;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("ui");
  const [taskId, setTaskId] = useState("");
  const [compositeTaskId, setCompositeTaskId] = useState<string | null>(null);
  const testMutation = useTriggerIntegrationTest();
  const { data: recentTasks } = useRecentIntegrationTasks(platform);
  const { data: eventStatus } = useIntegrationEventStatus(compositeTaskId, {
    enabled: !!compositeTaskId,
  });

  // Reset state when platform changes (tab switch)
  useEffect(() => {
    setTaskId("");
    setCompositeTaskId(null);
    testMutation.reset();
  }, [platform]);

  const status = eventStatus as
    | {
        state?: string;
        run_count?: number;
        task_name?: string;
        replies?: { type: string; content: string }[];
        createdAt?: string;
        updatedAt?: string;
      }
    | undefined;

  const handleRunTest = async () => {
    try {
      const result: { compositeTaskId?: string } =
        (await testMutation.mutateAsync({ platform, taskId })) as any;
      if (result?.compositeTaskId) {
        setCompositeTaskId(result.compositeTaskId);
      }
    } catch {
      // error state handled by mutation
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTaskId("");
    setCompositeTaskId(null);
    testMutation.reset();
  };

  const isDone = status?.state === "completed" || status?.state === "failed";
  const isRunning = compositeTaskId && !isDone;
  const placeholder =
    platform === "wrike" ? "e.g. IEAG2JQWI" : "e.g. 1234567890";

  const replyCardClass = (type: string) => {
    switch (type) {
      case "error_message":
        return "rounded border border-red-200 bg-red-50 p-3";
      case "completion":
        return "rounded border border-green-200 bg-green-50 p-3";
      case "retry_prompt":
        return "rounded border border-yellow-200 bg-yellow-50 p-3";
      default:
        return "rounded border border-gray-200 bg-gray-50 p-3";
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="500px">
        <Dialog.Title>
          Test{" "}
          {platform === "wrike"
            ? t("settings.integrationSettingsRoute.wrike")
            : t("settings.integrationSettingsRoute.asana")}{" "}
          Integration
        </Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Triggers a real integration workflow. The review will run and post a
          comment to the task (3-15 min).
        </Dialog.Description>

        <Flex direction="column" gap="4" mt="4">
          {/* Phase 1: Input */}
          {!compositeTaskId && !testMutation.isPending && (
            <>
              <Input
                data-testid="integration-test-dialog-task-input"
                label={t("settings.integrationSettingsRoute.taskId")}
                placeholder={placeholder}
                value={taskId}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setTaskId(e.target.value)
                }
              />
              {recentTasks && (recentTasks as any[]).length > 0 && (
                <div className="grid gap-1">
                  <Typography size="xs" className="text-gray-500">
                    {t("settings.integrationSettingsRoute.recentTasks")}
                  </Typography>
                  <div className="flex flex-wrap gap-1">
                    {(recentTasks as any[]).map((t: any) => (
                      <button
                        key={t.taskId}
                        data-testid={`integration-test-dialog-recent-task-${t.taskId}`}
                        type="button"
                        className="hover:bg-gray-100 rounded border px-2 py-1 text-xs"
                        onClick={() => setTaskId(t.taskId)}
                      >
                        {t.taskName || t.taskId}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {testMutation.isError && (
                <Typography size="xs" className="text-red-500">
                  {testMutation.error instanceof Error
                    ? testMutation.error.message
                    : t(
                        "settings.integrationSettingsRoute.unknownErrorOccurred",
                      )}
                </Typography>
              )}
            </>
          )}

          {/* Phase 2: Running */}
          {(testMutation.isPending || isRunning) && (
            <div className="grid gap-3">
              <div className="flex items-center gap-2">
                <Spinner size={16} className="animate-spin" />
                <Typography size="sm">
                  {testMutation.isPending
                    ? t("settings.integrationSettingsRoute.startingTest")
                    : `Status: ${status?.state || "pending"}`}
                </Typography>
              </div>
              {compositeTaskId && (
                <Typography size="xs" className="font-mono text-gray-400">
                  {compositeTaskId}
                </Typography>
              )}
              <Typography size="xs" className="text-gray-500">
                {t(
                  "settings.integrationSettingsRoute.canCloseDialogTestContinues",
                )}
              </Typography>
              <ReplyList
                replies={status?.replies}
                replyCardClass={replyCardClass}
              />
            </div>
          )}

          {/* Phase 3: Done */}
          {isDone && (
            <div className="grid gap-3">
              <div className="flex items-center gap-2">
                <Chip
                  label={status?.state === "completed" ? "Completed" : "Failed"}
                  variant={
                    status?.state === "completed" ? "malachite" : "vermillion"
                  }
                  size="sm"
                />
                {status?.task_name && (
                  <Typography size="sm" className="text-gray-600">
                    {status.task_name}
                  </Typography>
                )}
              </div>
              <ReplyList
                replies={status?.replies}
                replyCardClass={replyCardClass}
              />
            </div>
          )}
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          {!compositeTaskId && !testMutation.isPending ? (
            <>
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  {t("common:actions.cancel")}
                </Button>
              </Dialog.Close>
              <Button
                data-testid="integration-test-dialog-run-button"
                onClick={handleRunTest}
                disabled={!taskId.trim()}
              >
                {t("settings.integrationSettingsRoute.runTest")}
              </Button>
            </>
          ) : (
            <Button
              data-testid="integration-test-dialog-close-button"
              variant="soft"
              color="gray"
              onClick={handleClose}
            >
              {t("common:actions.close")}
            </Button>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function AsanaProjectSelector({
  webhooks,
  isEnabled,
  isConnected,
}: {
  webhooks: NonNullable<IntegrationConfig["asana"]>["webhooks"];
  isEnabled: boolean;
  isConnected: boolean;
}) {
  const { t } = useTranslation("ui");
  const { notify } = useUI();
  const {
    data: projects,
    isLoading: loadingProjects,
    refetch: refetchProjects,
  } = useAsanaProjects(isEnabled && isConnected);
  const registerMutation = useRegisterAsanaWebhook();
  const removeMutation = useRemoveAsanaWebhook();

  const activeWebhooks = (webhooks || [])
    .filter((w) => w.webhookId !== "__pending__")
    .slice()
    .sort((a, b) => {
      // Newest subscription first; fall back to stable order if createdAt missing
      const at = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bt = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bt - at;
    });
  const subscribedGids = new Set(activeWebhooks.map((w) => w.projectGid));
  const available = ((projects as any[]) || []).filter(
    (p: any) => !subscribedGids.has(p.gid),
  );

  const handleSubscribe = async (project: { gid: string; name: string }) => {
    try {
      await registerMutation.mutateAsync({
        projectGid: project.gid,
        projectName: project.name,
      });
      notify({ title: `Subscribed to ${project.name}`, variant: "success" });
    } catch {
      notify({ title: "Failed to subscribe", variant: "error" });
    }
  };

  const handleUnsubscribe = async (webhookId: string, projectName: string) => {
    try {
      await removeMutation.mutateAsync(webhookId);
      notify({
        title: `Unsubscribed from ${projectName}`,
        variant: "success",
      });
    } catch {
      notify({ title: "Failed to unsubscribe", variant: "error" });
    }
  };

  if (!isEnabled || !isConnected) return null;

  return (
    <Card className="mb-4">
      <div className="grid gap-4 p-4">
        <Typography as="h2" size="lg" weight="bold">
          {t("settings.integrationSettingsRoute.projectWebhooks")}
        </Typography>
        <Typography size="sm" className="text-gray-500">
          {t(
            "settings.integrationSettingsRoute.subscribeAsanaProjectsRealTime",
          )}
        </Typography>

        {activeWebhooks.length > 0 && (
          <div>
            <Typography size="sm" weight="bold" className="mb-2">
              {t("settings.integrationSettingsRoute.activeSubscriptions")}
            </Typography>
            <div className="grid gap-2">
              {activeWebhooks.map((w) => (
                <div
                  key={w.webhookId}
                  className="flex items-center justify-between rounded border p-3"
                >
                  <div>
                    <Typography size="sm" weight="bold">
                      {w.projectName}
                    </Typography>
                    <Typography size="xs" className="text-gray-400">
                      {w.projectGid}
                    </Typography>
                  </div>
                  <Button
                    data-testid={`asana-webhook-remove-${w.webhookId}`}
                    variant="outline"
                    color="red"
                    size="1"
                    disabled={removeMutation.isPending}
                    onClick={() =>
                      handleUnsubscribe(w.webhookId, w.projectName)
                    }
                  >
                    {t("common:actions.remove")}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="mb-2 flex items-center justify-between">
            <Typography size="sm" weight="bold">
              {t("settings.integrationSettingsRoute.availableProjects")}
            </Typography>
            <Button
              data-testid="asana-webhooks-refresh-button"
              variant="ghost"
              size="1"
              onClick={() => refetchProjects()}
              disabled={loadingProjects}
            >
              {t("common:actions.refresh")}
            </Button>
          </div>
          {loadingProjects ? (
            <div className="flex items-center gap-2 p-3">
              <Spinner size={16} className="animate-spin" />
              <Typography size="sm">
                {t("settings.integrationSettingsRoute.loadingProjects")}
              </Typography>
            </div>
          ) : available.length === 0 ? (
            <Typography size="xs" className="text-gray-400 p-3">
              {(projects as any[])?.length
                ? t(
                    "settings.integrationSettingsRoute.allProjectsAlreadySubscribed",
                  )
                : t(
                    "settings.integrationSettingsRoute.noProjectsFoundEnsureAsana",
                  )}
            </Typography>
          ) : (
            <div className="grid gap-2">
              {available.map((p: any) => (
                <div
                  key={p.gid}
                  className="flex items-center justify-between rounded border p-3"
                >
                  <div>
                    <Typography size="sm">{p.name}</Typography>
                    {p.workspace && (
                      <Typography size="xs" className="text-gray-400">
                        {p.workspace}
                      </Typography>
                    )}
                  </div>
                  <Button
                    data-testid={`asana-webhook-subscribe-${p.gid}`}
                    variant="outline"
                    size="1"
                    disabled={registerMutation.isPending}
                    onClick={() =>
                      handleSubscribe({ gid: p.gid, name: p.name })
                    }
                  >
                    {t("settings.integrationSettingsRoute.subscribe")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function AsanaEmailProjectAllowlist({
  value,
  onChange,
  isEnabled,
  isConnected,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  isEnabled: boolean;
  isConnected: boolean;
}) {
  const { t } = useTranslation("ui");
  const { data: projects, isLoading: loadingProjects } = useAsanaProjects(
    isEnabled && isConnected,
  );

  if (!isEnabled) return null;

  const allowed = new Set(value);
  const projectList =
    (projects as { gid: string; name: string }[] | undefined) || [];
  const knownGids = new Set(projectList.map((p) => p.gid));
  const unknownAllowedGids = value.filter((gid) => !knownGids.has(gid));

  const toggle = (gid: string, checked: boolean) => {
    if (checked) {
      if (!allowed.has(gid)) onChange([...value, gid]);
    } else {
      onChange(value.filter((g) => g !== gid));
    }
  };

  return (
    <Card className="mb-4">
      <div className="grid gap-3 p-4">
        <div>
          <Typography size="sm" weight="bold">
            {t("settings.integrationSettingsRoute.emailProjectAllowlist")}
          </Typography>
          <Typography size="xs" className="text-gray-500">
            Restrict inbound Asana email processing to specific projects. Leave
            all unchecked to process emails from every project (default).
          </Typography>
        </div>

        {loadingProjects ? (
          <div
            className="flex items-center gap-2 p-2"
            data-testid="asana-email-allowlist-loading"
          >
            <Spinner size={16} className="animate-spin" />
            <Typography size="sm">
              {t("settings.integrationSettingsRoute.loadingAllowlistProjects")}
            </Typography>
          </div>
        ) : projectList.length === 0 ? (
          <div data-testid="asana-email-allowlist-empty">
            <Typography size="xs" className="text-gray-400 p-2">
              {t(
                "settings.integrationSettingsRoute.connectAsanaPopulateAllowlist",
              )}
            </Typography>
          </div>
        ) : (
          <div
            className="grid gap-2"
            role="group"
            aria-label={t(
              "settings.integrationSettingsRoute.asanaEmailProjectAllowlist",
            )}
            data-testid="asana-email-allowlist"
          >
            {projectList.map((p) => {
              const checked = allowed.has(p.gid);
              const checkboxId = `asana-email-allowlist-${p.gid}`;
              return (
                <label
                  key={p.gid}
                  htmlFor={checkboxId}
                  className="flex cursor-pointer items-center gap-2"
                  data-testid={`asana-email-allowlist-row-${p.gid}`}
                >
                  <input
                    id={checkboxId}
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => toggle(p.gid, e.target.checked)}
                    aria-label={`Allow Asana emails for ${p.name}`}
                    data-testid={`asana-email-allowlist-checkbox-${p.gid}`}
                  />
                  <div>
                    <Typography size="sm">{p.name}</Typography>
                    <Typography size="xs" className="text-gray-400">
                      {p.gid}
                    </Typography>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {unknownAllowedGids.length > 0 && (
          <div data-testid="asana-email-allowlist-unknown">
            <Typography size="xs" className="text-gray-400">
              {unknownAllowedGids.length} project gid
              {unknownAllowedGids.length === 1 ? "" : "s"} in your allowlist not
              shown above (archived or in a different workspace): preserved on
              save.
            </Typography>
          </div>
        )}

        {value.length === 0 && projectList.length > 0 && (
          <div data-testid="asana-email-allowlist-all">
            <Typography size="xs" className="text-gray-500">
              {t(
                "settings.integrationSettingsRoute.processingEmailsFromAllProjects",
              )}
            </Typography>
          </div>
        )}
      </div>
    </Card>
  );
}

function IntegrationSettings() {
  const { t } = useTranslation("ui");
  const { data, isLoading } = useIntegrationSettings();
  const updateMutation = useUpdateIntegrationSettings();
  const { data: health } = useIntegrationHealth();
  const { notify } = useUI();
  const navigate = useNavigate();

  const [config, setConfig] = useState<IntegrationConfig>({});
  const [activeTab, setActiveTab] = useState<IntegrationTab>("wrike");
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [testDialogOpen, setTestDialogOpen] = useState(false);

  useEffect(() => {
    if (data) {
      setConfig(data as IntegrationConfig);
      setIsDirty(false);
    }
  }, [data]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Trigger config: eventType and statusName must be set together
    if (activeTab === "wrike") {
      const trigger = config.wrike?.triggerConfig;
      if (trigger?.eventType && !trigger?.statusName) {
        newErrors["statusName"] =
          "Status Name is required when Event Type is set";
      }
      if (trigger?.statusName && !trigger?.eventType) {
        newErrors["eventType"] =
          "Event Type is required when Status Name is set";
      }
    }

    // Design review rules: descriptionContains must be non-empty (wrike/asana only)
    if (activeTab !== "email") {
      const rules = config[activeTab]?.designReviewRules || [];
      rules.forEach((rule, index) => {
        if (!rule.descriptionContains.trim()) {
          newErrors[`rule-${index}`] = "Description match text is required";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      await updateMutation.mutateAsync(config);
      setIsDirty(false);
      notify({ title: "Integration settings saved", variant: "success" });
    } catch (error) {
      notify({
        title: "Error saving integration settings",
        message:
          error instanceof Error ? error.message : "An unknown error occurred",
        variant: "error",
      });
    }
  };

  const clearFieldError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const updateEnabled = (platform: IntegrationTab, value: boolean) => {
    setIsDirty(true);
    setConfig((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        enabled: value,
      },
    }));
  };

  const updateTriggerConfig = (field: keyof TriggerConfig, value: any) => {
    setIsDirty(true);
    clearFieldError(field);
    setConfig((prev) => ({
      ...prev,
      wrike: {
        ...prev.wrike,
        triggerConfig: {
          ...prev.wrike?.triggerConfig,
          [field]: value,
        },
      },
    }));
  };

  const updateCommentBehavior = (
    platform: "wrike" | "asana",
    field: keyof CommentBehavior,
    value: boolean,
  ) => {
    setIsDirty(true);
    setConfig((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        commentBehavior: {
          ...prev[platform]?.commentBehavior,
          [field]: value,
        },
      },
    }));
  };

  const updateEmailReplySettings = (
    field: keyof EmailReplySettings,
    value: any,
  ) => {
    setIsDirty(true);
    setConfig((prev) => ({
      ...prev,
      email: {
        ...prev.email,
        replySettings: {
          ...prev.email?.replySettings,
          [field]: value,
        },
      },
    }));
  };

  const updateEmailListField = (
    field: "allowedDomains" | "allowedSenderPatterns",
    value: string,
  ) => {
    setIsDirty(true);
    const items = value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    setConfig((prev) => ({
      ...prev,
      email: {
        ...prev.email,
        [field]: items.length > 0 ? items : undefined,
      },
    }));
  };

  const updateDesignReviewRule = (
    platform: "wrike" | "asana",
    index: number,
    field: keyof DesignReviewRule,
    value: any,
  ) => {
    setIsDirty(true);
    if (field === "descriptionContains") {
      clearFieldError(`rule-${index}`);
    }
    setConfig((prev) => {
      const rules = [...(prev[platform]?.designReviewRules || [])];
      rules[index] = { ...rules[index], [field]: value };
      return {
        ...prev,
        [platform]: {
          ...prev[platform],
          designReviewRules: rules,
        },
      };
    });
  };

  const addDesignReviewRule = (platform: "wrike" | "asana") => {
    setIsDirty(true);
    setConfig((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        designReviewRules: [
          ...(prev[platform]?.designReviewRules || []),
          {
            descriptionContains: "",
            tagUsersOnCompletion: false,
            skipStatusUpdate: false,
          },
        ],
      },
    }));
  };

  const removeDesignReviewRule = (
    platform: "wrike" | "asana",
    index: number,
  ) => {
    if (!window.confirm("Remove this rule?")) return;
    setIsDirty(true);
    clearFieldError(`rule-${index}`);
    setConfig((prev) => {
      const rules = [...(prev[platform]?.designReviewRules || [])];
      rules.splice(index, 1);
      return {
        ...prev,
        [platform]: {
          ...prev[platform],
          designReviewRules: rules,
        },
      };
    });
  };

  if (isLoading) {
    return (
      <div
        className="h-full overflow-auto"
        data-testid="integration-settings-page"
      >
        <article className="flex flex-col gap-4 px-10 py-6">
          <Typography as="h1" size="2xl" weight="bold">
            {t("settings.integrationSettingsRoute.integrationSettings")}
          </Typography>
          <div className="flex items-center gap-2">
            <Spinner size={24} className="animate-spin" />
            <Typography>
              {t("settings.integrationSettingsRoute.loadingSettings")}
            </Typography>
          </div>
        </article>
      </div>
    );
  }

  const currentPlatform = config[activeTab];
  const isEnabled =
    activeTab === "email"
      ? currentPlatform?.enabled === true
      : currentPlatform?.enabled !== false;
  const commentBehavior =
    (activeTab !== "email" && (currentPlatform as any)?.commentBehavior) || {};
  const skipAll = commentBehavior.skipAllComments || false;
  const designReviewRules: DesignReviewRule[] =
    activeTab !== "email"
      ? (config[activeTab] as any)?.designReviewRules || []
      : [];

  const selectedEventType = EVENT_TYPE_OPTIONS.find(
    (o) => o.value === config.wrike?.triggerConfig?.eventType,
  );

  const healthData = health as
    | { wrike?: any; asana?: any; email?: any }
    | undefined;

  return (
    <div
      className="h-full overflow-auto"
      data-testid="integration-settings-page"
    >
      <article className="flex flex-col gap-4 px-10 py-6">
        <button
          data-testid="integration-settings-back-btn"
          onClick={() => navigate({ to: settingsRoute.to })}
          className="text-gray-500 hover:text-gray-700 flex items-center gap-2"
        >
          <ArrowLeft size={16} />
          <Typography size="sm">
            {t("settings.integrationSettingsRoute.backSettings")}
          </Typography>
        </button>

        <header className="flex items-center justify-between">
          <Typography as="h1" size="2xl" weight="bold">
            {t("settings.integrationSettingsRoute.integrationSettings")}
          </Typography>
          <Button
            data-testid="integration-settings-save-btn"
            onClick={handleSave}
            disabled={updateMutation.isPending || !isDirty}
          >
            {updateMutation.isPending
              ? t("common:status.savingDots")
              : isDirty
                ? t("settings.integrationSettingsRoute.saveChanges")
                : t("common:actions.save")}
          </Button>
        </header>

        {/* Connection Status — all platforms */}
        <ComposioStatusCard
          wrike={healthData?.wrike}
          asana={healthData?.asana}
          email={healthData?.email}
        />

        {/* Platform Tabs */}
        <Tabs.Root
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as IntegrationTab)}
        >
          <Tabs.List>
            <Tabs.Trigger
              data-testid="integration-settings-tab-wrike"
              value="wrike"
            >
              Wrike
              <HealthBadge status={healthData?.wrike} />
            </Tabs.Trigger>
            <Tabs.Trigger
              data-testid="integration-settings-tab-asana"
              value="asana"
            >
              Asana
              <HealthBadge status={healthData?.asana} />
            </Tabs.Trigger>
            <Tabs.Trigger value="email">
              <Envelope size={16} className="mr-1" />
              Email
              <EmailHealthBadge status={healthData?.email} />
            </Tabs.Trigger>
          </Tabs.List>

          <div className="mt-2">
            {/* Enable/Disable Toggle + Test Button */}
            <Card className="mb-4">
              <div className="flex items-center justify-between p-4">
                <div>
                  <Typography size="sm" weight="bold">
                    Enable{" "}
                    {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}{" "}
                    Integration
                  </Typography>
                  <Typography size="xs" className="text-gray-500">
                    {activeTab === "email"
                      ? isEnabled
                        ? t(
                            "settings.integrationSettingsRoute.emailIntegrationActiveUsersCan",
                          )
                        : t(
                            "settings.integrationSettingsRoute.emailIntegrationDisabledInboundEmails",
                          )
                      : isEnabled
                        ? t(
                            "settings.integrationSettingsRoute.integrationActiveWillProcessIncoming",
                          )
                        : t(
                            "settings.integrationSettingsRoute.integrationDisabledIncomingEventsWill",
                          )}
                  </Typography>
                </div>
                <div className="flex items-center gap-3">
                  {isEnabled && activeTab !== "email" && (
                    <Button
                      data-testid="integration-settings-test-btn"
                      variant="outline"
                      onClick={() => setTestDialogOpen(true)}
                    >
                      {t("settings.integrationSettingsRoute.testIntegration")}
                    </Button>
                  )}
                  <Toggle
                    data-testid="integration-settings-enable-toggle"
                    label={
                      <span className="sr-only">
                        {t(
                          "settings.integrationSettingsRoute.enableIntegration",
                        )}
                      </span>
                    }
                    value="enabled"
                    checked={isEnabled}
                    onChange={(e) => updateEnabled(activeTab, e.target.checked)}
                  />
                </div>
              </div>
            </Card>

            {/* Asana-specific sub-toggle: inbound email (no-reply@asana.com) */}
            {activeTab === "asana" && isEnabled && (
              <Card className="mb-4">
                <div className="flex items-center justify-between p-4">
                  <div>
                    <Typography size="sm" weight="bold">
                      {t(
                        "settings.integrationSettingsRoute.acceptAsanaEmailNotifications",
                      )}
                    </Typography>
                    <Typography size="xs" className="text-gray-500">
                      When on, emails from no-reply@asana.com (forwarded from
                      Asana notifications) trigger AI reviews for the referenced
                      task. Turn off to ignore those emails while keeping
                      webhook events active.
                    </Typography>
                  </div>
                  <Toggle
                    data-testid="integration-settings-asana-email-toggle"
                    label={
                      <span className="sr-only">
                        {t(
                          "settings.integrationSettingsRoute.enableAsanaEmailIntegration",
                        )}
                      </span>
                    }
                    value="emailEnabled"
                    checked={config.asana?.emailEnabled !== false}
                    onChange={(e) => {
                      setIsDirty(true);
                      setConfig((prev) => ({
                        ...prev,
                        asana: {
                          ...prev.asana,
                          emailEnabled: e.target.checked,
                        },
                      }));
                    }}
                  />
                </div>
              </Card>
            )}

            {/* Asana email project allowlist: restrict which projects' emails
              get processed. Shared Asana accounts (e.g. one workspace used by
              two enterprises) leak notifications without this. */}
            {activeTab === "asana" && isEnabled && (
              <AsanaEmailProjectAllowlist
                data-testid="asana-email-allowlist-section"
                value={config.asana?.allowedProjectGids || []}
                onChange={(next) => {
                  setIsDirty(true);
                  setConfig((prev) => ({
                    ...prev,
                    asana: {
                      ...prev.asana,
                      allowedProjectGids: next,
                    },
                  }));
                }}
                isEnabled={isEnabled}
                isConnected={Boolean(healthData?.asana?.connected)}
              />
            )}

            {/* Email-specific: AI Email Address display */}
            {activeTab === "email" && healthData?.email?.aiEmailAddress && (
              <Card className="mb-4">
                <div className="grid gap-2 p-4">
                  <Typography size="sm" weight="bold">
                    {t("settings.integrationSettingsRoute.aiEmailAddress")}
                  </Typography>
                  <Typography size="xs" className="text-gray-500">
                    {t(
                      "settings.integrationSettingsRoute.usersSendEmailsAddressTrigger",
                    )}
                  </Typography>
                  <div className="bg-gray-50 flex items-center justify-between rounded border p-3">
                    <Typography size="sm" className="font-mono">
                      {healthData.email.aiEmailAddress}
                    </Typography>
                    <button
                      data-testid="email-address-copy-button"
                      aria-label={t(
                        "settings.integrationSettingsRoute.copyEmailAddress",
                      )}
                      className="text-gray-600 hover:text-gray-800 flex items-center gap-1 text-sm"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          healthData.email.aiEmailAddress,
                        );
                        notify({
                          title: "Email address copied",
                          variant: "success",
                        });
                      }}
                    >
                      <Copy size={14} />
                      Copy
                    </button>
                  </div>
                </div>
              </Card>
            )}
            {activeTab === "email" &&
              !healthData?.email?.aiEmailAddress &&
              config.email?.enabled && (
                <Card className="mb-4">
                  <div className="p-4">
                    <Typography size="xs" className="text-gray-400">
                      {t(
                        "settings.integrationSettingsRoute.saveSettingsGenerateInboundAddress",
                      )}
                    </Typography>
                  </div>
                </Card>
              )}

            {/* Attachment Limits — Email only */}
            {activeTab === "email" && (
              <Card className="mb-4">
                <div className="grid gap-2 p-4">
                  <Typography size="sm" weight="bold">
                    {t("settings.integrationSettingsRoute.attachmentLimits")}
                  </Typography>
                  <Typography size="xs" className="text-gray-500">
                    Maximum file size per attachment: 25 MB. For larger files,
                    include a download link in the email body instead.
                  </Typography>
                </div>
              </Card>
            )}

            <div className={!isEnabled ? "pointer-events-none opacity-50" : ""}>
              {/* Email Reply Settings — Email only */}
              {activeTab === "email" && (
                <Card className="mb-4">
                  <div className="grid gap-4 p-4">
                    <Typography as="h2" size="lg" weight="bold">
                      {t("settings.integrationSettingsRoute.replySettings")}
                    </Typography>
                    <Typography size="sm" className="text-gray-500">
                      Configure what gets included in the email reply after a
                      review completes.
                    </Typography>

                    <div className="grid gap-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <Typography size="sm" weight="bold">
                            {t(
                              "settings.integrationSettingsRoute.replyReviewLink",
                            )}
                          </Typography>
                          <Typography size="xs" className="text-gray-500">
                            Include a link to the full review in the reply
                            email.
                          </Typography>
                        </div>
                        <Toggle
                          data-testid="email-reply-with-link-toggle"
                          label={
                            <span className="sr-only">
                              {t(
                                "settings.integrationSettingsRoute.replyReviewLink",
                              )}
                            </span>
                          }
                          value="replyWithLink"
                          checked={
                            config.email?.replySettings?.replyWithLink !== false
                          }
                          onChange={(e) =>
                            updateEmailReplySettings(
                              "replyWithLink",
                              e.target.checked,
                            )
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Typography size="sm" weight="bold">
                            {t(
                              "settings.integrationSettingsRoute.includeAiCommentsReply",
                            )}
                          </Typography>
                          <Typography size="xs" className="text-gray-500">
                            Add a summary of AI review comments in the reply
                            email body.
                          </Typography>
                        </div>
                        <Toggle
                          data-testid="email-reply-with-comments-toggle"
                          label={
                            <span className="sr-only">
                              {t(
                                "settings.integrationSettingsRoute.includeAiCommentsReply",
                              )}
                            </span>
                          }
                          value="replyWithComments"
                          checked={
                            config.email?.replySettings?.replyWithComments ||
                            false
                          }
                          onChange={(e) =>
                            updateEmailReplySettings(
                              "replyWithComments",
                              e.target.checked,
                            )
                          }
                        />
                      </div>

                      {config.email?.replySettings?.replyWithComments && (
                        <div className="grid gap-2">
                          <Typography size="sm" weight="bold">
                            {t("settings.integrationSettingsRoute.replyPrompt")}
                          </Typography>
                          <Typography size="xs" className="text-gray-500">
                            Instructions for what the AI should include in the
                            email reply body.
                          </Typography>
                          <Textarea
                            data-testid="email-reply-prompt-textarea"
                            aria-label={t(
                              "settings.integrationSettingsRoute.replyPrompt2",
                            )}
                            placeholder={DEFAULT_REPLY_PROMPT}
                            value={
                              config.email?.replySettings?.replyPrompt ||
                              DEFAULT_REPLY_PROMPT
                            }
                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                              updateEmailReplySettings(
                                "replyPrompt",
                                e.target.value,
                              )
                            }
                            rows={3}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              )}

              {/* Sender Restrictions — Email only */}
              {activeTab === "email" && (
                <Card className="mb-4">
                  <div className="grid gap-3 p-4">
                    <Typography as="h2" size="lg" weight="bold">
                      {t(
                        "settings.integrationSettingsRoute.senderRestrictions",
                      )}
                    </Typography>
                    <Typography size="sm" className="text-gray-500">
                      Restrict which senders can trigger reviews. Leave both
                      empty to allow all enterprise users.
                    </Typography>
                    <Input
                      data-testid="email-allowed-domains-input"
                      label={t(
                        "settings.integrationSettingsRoute.allowedDomainsCommaSeparated",
                      )}
                      placeholder={t(
                        "settings.integrationSettingsRoute.eGAcmeComPartner",
                      )}
                      value={config.email?.allowedDomains?.join(", ") || ""}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        updateEmailListField("allowedDomains", e.target.value)
                      }
                    />
                    <Input
                      data-testid="email-allowed-sender-patterns-input"
                      label={t(
                        "settings.integrationSettingsRoute.allowedSenderPatternsCommaSeparated",
                      )}
                      placeholder={t(
                        "settings.integrationSettingsRoute.eGJohnAcmeCom",
                      )}
                      value={
                        config.email?.allowedSenderPatterns?.join(", ") || ""
                      }
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        updateEmailListField(
                          "allowedSenderPatterns",
                          e.target.value,
                        )
                      }
                    />
                    <Typography size="xs" className="text-gray-400">
                      A sender is allowed if their domain matches OR their full
                      email matches any pattern.
                    </Typography>
                  </div>
                </Card>
              )}

              {/* Trigger Configuration - Wrike only */}
              {activeTab === "wrike" && (
                <Card className="mb-4">
                  <div className="grid gap-4 p-4">
                    <Typography as="h2" size="lg" weight="bold">
                      {t(
                        "settings.integrationSettingsRoute.triggerConfiguration",
                      )}
                    </Typography>
                    <Typography size="sm" className="text-gray-500">
                      Configure when a review gets triggered for this
                      integration.
                    </Typography>

                    <div className="grid gap-3">
                      <Select<false>
                        data-testid="integration-settings-event-type-select"
                        label={t("settings.integrationSettingsRoute.eventType")}
                        placeholder={t(
                          "settings.integrationSettingsRoute.selectEventType",
                        )}
                        options={EVENT_TYPE_OPTIONS}
                        value={selectedEventType}
                        onChange={(option) =>
                          updateTriggerConfig("eventType", option?.value || "")
                        }
                      />
                      {errors["eventType"] && (
                        <Typography size="xs" className="text-red-500">
                          {errors["eventType"]}
                        </Typography>
                      )}
                      <Input
                        data-testid="integration-settings-status-input"
                        label={t(
                          "settings.integrationSettingsRoute.statusName",
                        )}
                        placeholder={t(
                          "settings.integrationSettingsRoute.eGInternalQc",
                        )}
                        value={config.wrike?.triggerConfig?.statusName || ""}
                        invalid={!!errors["statusName"]}
                        helpText={errors["statusName"] || ""}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          updateTriggerConfig("statusName", e.target.value)
                        }
                      />

                      <div className="flex items-center justify-between">
                        <div>
                          <Typography size="sm" weight="bold">
                            {t(
                              "settings.integrationSettingsRoute.skipPunttUserCheck",
                            )}
                          </Typography>
                          <Typography size="xs" className="text-gray-500">
                            Trigger for all tasks, not just those assigned to
                            the Puntt user.
                          </Typography>
                        </div>
                        <Toggle
                          data-testid="integration-settings-skip-user-check-toggle"
                          label={
                            <span className="sr-only">
                              {t(
                                "settings.integrationSettingsRoute.skipPunttUserCheck",
                              )}
                            </span>
                          }
                          value="skipPunttUserCheck"
                          checked={
                            config.wrike?.triggerConfig?.skipPunttUserCheck ||
                            false
                          }
                          onChange={(e) =>
                            updateTriggerConfig(
                              "skipPunttUserCheck",
                              e.target.checked,
                            )
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Typography size="sm" weight="bold">
                            {t(
                              "settings.integrationSettingsRoute.assignSelfTrigger",
                            )}
                          </Typography>
                          <Typography size="xs" className="text-gray-500">
                            {t(
                              "settings.integrationSettingsRoute.assignPunttUserTaskWhen",
                            )}
                          </Typography>
                        </div>
                        <Toggle
                          data-testid="integration-settings-assign-self-toggle"
                          label={
                            <span className="sr-only">
                              {t(
                                "settings.integrationSettingsRoute.assignSelfTrigger",
                              )}
                            </span>
                          }
                          value="assignSelfOnTrigger"
                          checked={
                            config.wrike?.triggerConfig?.assignSelfOnTrigger ||
                            false
                          }
                          onChange={(e) =>
                            updateTriggerConfig(
                              "assignSelfOnTrigger",
                              e.target.checked,
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Asana Project Webhooks */}
              {activeTab === "asana" && (
                <AsanaProjectSelector
                  webhooks={config.asana?.webhooks}
                  isEnabled={isEnabled}
                  isConnected={healthData?.asana?.connected === true}
                />
              )}

              {/* Comment/Response Behavior — Wrike/Asana only */}
              {activeTab !== "email" && (
                <Card className="mb-4">
                  <div className="grid gap-4 p-4">
                    <Typography as="h2" size="lg" weight="bold">
                      {t("settings.integrationSettingsRoute.responseBehavior")}
                    </Typography>
                    <Typography size="sm" className="text-gray-500">
                      Control how the integration responds with comments and
                      links.
                    </Typography>

                    <div className="grid gap-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <Typography size="sm" weight="bold">
                            {t(
                              "settings.integrationSettingsRoute.skipAllComments",
                            )}
                          </Typography>
                          <Typography size="xs" className="text-gray-500">
                            {t(
                              "settings.integrationSettingsRoute.disableAllCommentPostingIntegration",
                            )}
                          </Typography>
                        </div>
                        <Toggle
                          data-testid="integration-settings-skip-comments-toggle"
                          label={
                            <span className="sr-only">
                              {t(
                                "settings.integrationSettingsRoute.skipAllComments",
                              )}
                            </span>
                          }
                          value="skipAllComments"
                          checked={skipAll}
                          onChange={(e) =>
                            updateCommentBehavior(
                              activeTab,
                              "skipAllComments",
                              e.target.checked,
                            )
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Typography
                            size="sm"
                            weight="bold"
                            className={skipAll ? "text-gray-400" : ""}
                          >
                            {t(
                              "settings.integrationSettingsRoute.postProgressComments",
                            )}
                          </Typography>
                          <Typography size="xs" className="text-gray-500">
                            {skipAll
                              ? t(
                                  "settings.integrationSettingsRoute.disabledBecauseSkipAllComments",
                                )
                              : t(
                                  "settings.integrationSettingsRoute.postCommentsWhenReviewStarts",
                                )}
                          </Typography>
                        </div>
                        <Toggle
                          data-testid="integration-settings-progress-comments-toggle"
                          label={
                            <span className="sr-only">
                              {t(
                                "settings.integrationSettingsRoute.postProgressComments",
                              )}
                            </span>
                          }
                          value="postProgressComments"
                          checked={
                            skipAll
                              ? false
                              : commentBehavior.postProgressComments !== false
                          }
                          disabled={skipAll}
                          onChange={(e) =>
                            updateCommentBehavior(
                              activeTab,
                              "postProgressComments",
                              e.target.checked,
                            )
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Typography
                            size="sm"
                            weight="bold"
                            className={skipAll ? "text-gray-400" : ""}
                          >
                            {t(
                              "settings.integrationSettingsRoute.postCompletionComments",
                            )}
                          </Typography>
                          <Typography size="xs" className="text-gray-500">
                            {skipAll
                              ? t(
                                  "settings.integrationSettingsRoute.disabledBecauseSkipAllComments",
                                )
                              : t(
                                  "settings.integrationSettingsRoute.postCommentsWhenReviewComplete",
                                )}
                          </Typography>
                        </div>
                        <Toggle
                          data-testid="integration-settings-completion-comments-toggle"
                          label={
                            <span className="sr-only">
                              {t(
                                "settings.integrationSettingsRoute.postCompletionComments",
                              )}
                            </span>
                          }
                          value="postCompletionComments"
                          checked={
                            skipAll
                              ? false
                              : commentBehavior.postCompletionComments !== false
                          }
                          disabled={skipAll}
                          onChange={(e) =>
                            updateCommentBehavior(
                              activeTab,
                              "postCompletionComments",
                              e.target.checked,
                            )
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Typography size="sm" weight="bold">
                            {t(
                              "settings.integrationSettingsRoute.skipLinkUpdates",
                            )}
                          </Typography>
                          <Typography size="xs" className="text-gray-500">
                            {t(
                              "settings.integrationSettingsRoute.skipPostingReviewShareLinks",
                            )}
                          </Typography>
                        </div>
                        <Toggle
                          data-testid="integration-settings-skip-links-toggle"
                          label={
                            <span className="sr-only">
                              {t(
                                "settings.integrationSettingsRoute.skipLinkUpdates",
                              )}
                            </span>
                          }
                          value="skipLinkUpdates"
                          checked={commentBehavior.skipLinkUpdates || false}
                          onChange={(e) =>
                            updateCommentBehavior(
                              activeTab,
                              "skipLinkUpdates",
                              e.target.checked,
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Completion Rules (formerly Design Review Rules) — not yet implemented, wrike/asana only */}
              {activeTab !== "email" && (
                <Card>
                  <div className="pointer-events-none grid gap-4 p-4 opacity-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <Typography as="h2" size="lg" weight="bold">
                          Completion Rules{" "}
                          <span className="text-gray-400 text-sm font-normal">
                            {t("settings.integrationSettingsRoute.comingSoon")}
                          </span>
                        </Typography>
                        <Typography size="sm" className="text-gray-500">
                          After a review completes, each rule is checked in
                          order against the task description. The first match
                          determines what happens.
                        </Typography>
                      </div>
                      <Button
                        data-testid="design-review-add-rule-button"
                        variant="outline"
                        onClick={() => addDesignReviewRule(activeTab)}
                      >
                        {t("settings.integrationSettingsRoute.addRule")}
                      </Button>
                    </div>

                    {designReviewRules.length === 0 ? (
                      <Typography size="sm" className="text-gray-400">
                        {t(
                          "settings.integrationSettingsRoute.noCompletionRulesConfigured",
                        )}
                      </Typography>
                    ) : (
                      <div className="grid gap-3">
                        {designReviewRules.map((rule, index) => (
                          <div
                            key={index}
                            className="grid gap-3 rounded-lg border p-3"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="grid flex-1 gap-3">
                                <Input
                                  data-testid={`design-review-rule-text-${index}`}
                                  label={t(
                                    "settings.integrationSettingsRoute.matchTextTaskDescription",
                                  )}
                                  placeholder={t(
                                    "settings.integrationSettingsRoute.eGCorporateDesignReview",
                                  )}
                                  value={rule.descriptionContains}
                                  invalid={!!errors[`rule-${index}`]}
                                  helpText={
                                    errors[`rule-${index}`] ||
                                    "Case-sensitive substring match against the task description"
                                  }
                                  onChange={(
                                    e: ChangeEvent<HTMLInputElement>,
                                  ) =>
                                    updateDesignReviewRule(
                                      activeTab,
                                      index,
                                      "descriptionContains",
                                      e.target.value,
                                    )
                                  }
                                />

                                <div className="flex items-center justify-between">
                                  <div>
                                    <Typography size="sm" weight="bold">
                                      {t(
                                        "settings.integrationSettingsRoute.tagStakeholdersCompletion",
                                      )}
                                    </Typography>
                                    <Typography
                                      size="xs"
                                      className="text-gray-500"
                                    >
                                      @-mention configured tag users in the
                                      completion comment
                                    </Typography>
                                  </div>
                                  <Toggle
                                    data-testid={`design-review-rule-tag-users-${index}`}
                                    label={
                                      <span className="sr-only">
                                        {t(
                                          "settings.integrationSettingsRoute.tagStakeholdersCompletion",
                                        )}
                                      </span>
                                    }
                                    value={`tagUsers-${index}`}
                                    checked={rule.tagUsersOnCompletion || false}
                                    onChange={(e) =>
                                      updateDesignReviewRule(
                                        activeTab,
                                        index,
                                        "tagUsersOnCompletion",
                                        e.target.checked,
                                      )
                                    }
                                  />
                                </div>

                                <div className="flex items-center justify-between">
                                  <div>
                                    <Typography size="sm" weight="bold">
                                      {t(
                                        "settings.integrationSettingsRoute.skipTaskStatusUpdate",
                                      )}
                                    </Typography>
                                    <Typography
                                      size="xs"
                                      className="text-gray-500"
                                    >
                                      Don&apos;t change the task status when
                                      this rule matches
                                    </Typography>
                                  </div>
                                  <Toggle
                                    data-testid={`design-review-rule-skip-status-${index}`}
                                    label={
                                      <span className="sr-only">
                                        {t(
                                          "settings.integrationSettingsRoute.skipTaskStatusUpdate",
                                        )}
                                      </span>
                                    }
                                    value={`skipStatus-${index}`}
                                    checked={rule.skipStatusUpdate || false}
                                    onChange={(e) =>
                                      updateDesignReviewRule(
                                        activeTab,
                                        index,
                                        "skipStatusUpdate",
                                        e.target.checked,
                                      )
                                    }
                                  />
                                </div>

                                <RuleBehaviorPreview rule={rule} />
                              </div>
                              <Button
                                data-testid={`design-review-rule-remove-${index}`}
                                variant="outline"
                                color="red"
                                onClick={() =>
                                  removeDesignReviewRule(activeTab, index)
                                }
                              >
                                {t("common:actions.remove")}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="bg-gray-50 rounded p-2">
                      <Typography size="xs" className="text-gray-500">
                        If no rules match: status is updated but no comment is
                        posted.
                      </Typography>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </Tabs.Root>

        <TestIntegrationDialog
          platform={activeTab}
          open={testDialogOpen}
          onOpenChange={setTestDialogOpen}
        />
      </article>
    </div>
  );
}
